<template>
  <fts-design-modal
    width="1200px"
    class="fts-standard-activity-design-modal"
    ref="ftsDesignModal">
    <!-- title -->
    <template #title>
      <div class="design-modal-header-std__title">
        <div class="design-modal-header-name design-modal-header-name--flex-1">
          {{formData.name}}
        </div>
      </div>
    </template>
    <!-- description -->
    <template #description>
      <span class="design-modal-header-tag">Basic</span>
      <span class="design-modal-header-description">
        designing the form for standard activities like create, edit or view
      </span>
    </template>
    <!-- body -->
    <template #body>
      <!-- description -->
      <div class="design-modal-form-group design-modal-body-description" v-if="formData.description">
        {{formData.description}}
      </div>
      <!-- form design -->
      <fts-collapsible-element initial-visible class="dm-collapsible" v-if="standardWithWorkDesign">
        <!-- collapsible header -->
        <div
          slot="toggle"
          slot-scope="{ isVisible, toggle }"
          class="dm-collapsible-header"
        >
          <div class="dm-collapsible-header-wrapper">
            <div class="dm-collapsible-header__title">
              <!-- title -->
              <div class="dm-collapsible-header-title">
                Form
              </div>
              <!-- tag form -->
              <fts-tippy content="Remove form" v-if="formData.design">
                <div slot="trigger" class="dm-collapsible-header-tag-form">
                  <i class="fts-icon-form tag-form-icon" />
                  <font-awesome-icon
                    :icon="['fas', 'times']"
                    class="tag-form-action"
                    @click="removeWorkDesign"
                  />
                </div>
              </fts-tippy>
              <!-- tag action -->
              <fts-tippy
                :content="formDesignPreview ? 'switch to design mode' : 'switch to preview mode'" 
                v-if="formData.design && isVisible"
              >
                <div slot="trigger" class="dm-collapsible-header-tag-action" @click="updateFormDesignMode">
                  <div class="dm-collapsible-header-tag-label">
                    {{formDesignPreview ? "Design" : "Preview"}}
                  </div>
                </div>
              </fts-tippy>
            </div>
            <!-- error -->
            <div
              class="dm-collapsible-header-error"
              v-if="$v.formData && $v.formData.design && $v.formData.design.$error"
            >
              Form cannot be empty. Form at least must have information or element.
            </div>
            <!-- description -->
            <div class="dm-collapsible-header-description">
              designing an activity can be performed by user.
            </div>
          </div>
          <!-- actions -->
          <div class="dm-collapsible-header-actions">
            <!-- control -->
            <font-awesome-icon
              :icon="['fas', 'caret-right']"
              fixed-width
              class="dm-collapsible-control"
              :class="{ 'dm-collapsible-control--active': isVisible }"
              @click="toggle"
            />
          </div>
        </div>
        <!-- collapsible content -->
        <div slot="content" class="dm-collapsible-content">
          <!-- form designer -->
          <fts-form-designer
            :classification="formData.classification"
            :form="formData.design"
            :activityId="formData.id"
            :formData="classificationFormData"
            @previewChanged="(x) => formDesignPreview = x"
            ref="ftsFormDesigner"
            v-if="formData.design"
          />
          <!-- create work design -->
          <div class="dm-form-design-create" @click="createWorkDesign" v-else>
            <i class="fts-icon-form dm-form-design-create-icon" />
            <div>Click to add form</div>
          </div>
        </div>
      </fts-collapsible-element>
      <!-- flow design -->
      <fts-collapsible-element
        initial-visible 
        class="dm-collapsible" 
        v-if="formData.id === 'createEditView' || formData.id === 'create'"
      >
        <!-- collapsible header -->
        <div
          slot="toggle"
          slot-scope="{ isVisible, toggle }"
          class="dm-collapsible-header"
        >
          <div class="dm-collapsible-header-wrapper">
            <div class="dm-collapsible-header__title">
              <!-- title -->
              <div class="dm-collapsible-header-title">
                Flow
              </div>
              <!-- flow diagram -->
              <fts-tippy content="Open flow diagram">
                <div slot="trigger" class="dm-collapsible-header-tag-action" @click="openWorkflow">
                  <font-awesome-icon :icon="['fas', 'project-diagram']" class="dm-collapsible-header-tag-icon" />
                  <div class="dm-collapsible-header-tag-label">
                    Show Diagram
                  </div>
                </div>
              </fts-tippy>
            </div>
            <!-- description -->
            <div class="dm-collapsible-header-description">
              <div>
                User creates an entry by submitting form above. Indicate below whether the entry has an inistate everytime an new entry created. You may always change the configuration in a later time.
              </div>
              <div>
                Hint: For entry that does not have a process, you may safely ignore. If it is a workflow or process, you may start by creating the initial state.
              </div>
            </div>
          </div>
          <!-- actions -->
          <div class="dm-collapsible-header-actions">
            <!-- control -->
            <font-awesome-icon
              :icon="['fas', 'caret-right']"
              fixed-width
              class="dm-collapsible-control"
              :class="{ 'dm-collapsible-control--active': isVisible }"
              @click="toggle"
            />
          </div>
        </div>
        <!-- collapsible content -->
        <div slot="content" class="dm-collapsible-content">
          <div class="dm-flow-display">
            <div class="dm-flow-wrapper">
              <!-- start -->
              <div class="dm-flow-state-start">
                start
              </div>
              <!-- direction -->
              <font-awesome-icon :icon="['fas', 'long-arrow-alt-right']" class="dm-flow-display-direction" />
              <!-- activity -->
              <fts-activity-design-inline
                  :activity="{
                    name: 'create' 
                  }"
                  :navigatable="false"
                />
              <!-- direction -->
              <font-awesome-icon :icon="['fas', 'long-arrow-alt-right']" class="dm-flow-display-direction" />
              <!-- to state -->
              <fts-state-design-inline
                v-model="stateFormData.id"
                :list="states"
                initial-state
                :initial-edit-mode="!stateFormData.id"
                @navigate="navigateStateDesign"
                @navigate:activity="navigateNewActivityDesign"
              />
            </div>
          </div>
        </div>
      </fts-collapsible-element>
      <!-- advanced -->
      <fts-collapsible-element :condition="advanced" v-if="beta">
        <fts-btn 
          slot="toggle" 
          slot-scope="{isVisible, toggle}" 
          text 
          text-transform-init 
          :class="{'open': isVisible}"
          class="design-modal-advanced-toggle" 
          @click="toggle()">
          Advanced settings <font-awesome-icon :icon="['fas', 'caret-right']" class="design-modal-advanced-toggle-icon" />
        </fts-btn>
        <div slot="content" class="design-modal-advanced-content fts-row gutters">
          <!-- script -->
          <div class="fts-col design-modal-form-group" v-if="formData.design">
            <fts-form-script :form-data="formData.design" />
          </div>
          <!-- internal -->
          <fts-form-element
            :form-data="formData"
            :item="{
              fieldName: 'internal',
              input: 'toggleSwitch',
            }"         
            comfort-design
            class="fts-col" />
          <!-- skip history -->
          <fts-form-element
            :form-data="formData"
            :item="{
              fieldName: 'skipHistory',
              input: 'toggleSwitch',
            }"
            comfort-design 
            class="fts-col" />
          <!-- menus -->
          <fts-form-element
            :formData="formData"
            :item="{
              'fieldName': 'menus',
              'displayName': 'Listing',
              'icon': 'fts-icon-menu-caret-down',
            }"
            comfort-design
            class="fts-col">
            <fts-select
              slot="formElement"
              slot-scope="{formData, item}"
              :form-data="formData"
              :item="item"
              :options="menus"
              multiple
              comfort-design>
              <fts-label-group 
                slot="selected-option-container"
                slot-scope="{ option }"
                :item-id="option.id"
                :list="menus"
                emoji
              />
              <fts-label-group 
                slot="option"
                slot-scope="{ option }"
                :item-id="option.id"
                :list="menus"
                emoji
              />
            </fts-select>
          </fts-form-element>
        </div>
      </fts-collapsible-element>
    </template>
    <!-- footer -->
    <template #footerLeft>
      <!-- auth -->
      <fts-auth
        type="activity"
        :unit-id="formData.id"
        :title="formData.name"
        preview-placement="top"
      >
        <fts-btn
          slot="trigger"
          slot-scope="{ show, isRight }"
          text
          icon
          :color="isRight ? 'danger' : 'default'"
          @click="show"
        >
          <font-awesome-icon :icon="['fas', 'lock']" v-if="isRight" />
          <font-awesome-icon :icon="['fas', 'unlock']" v-else />
        </fts-btn>
      </fts-auth>
    </template>
    <template #footerRight>
      <!-- cancel -->
      <fts-btn text @click="beforeClose">Cancel</fts-btn>
      <!-- save -->
      <fts-btn color="primary" text @click="done(true)">
        <font-awesome-icon :icon="['fas', 'check']" /> Save
      </fts-btn>
      <!-- done -->
      <fts-btn outline color="primary" @click="done">
        <font-awesome-icon :icon="['fas', 'check']" /> Ok
      </fts-btn>
    </template>
  </fts-design-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);

import changesets from "diff-json";

import commons from "../commons/commons";
import canvasDesign from "../commons/canvasDesign";
import validator from "../commons/designValidator";

import FTSDesignModal from "./FTSDesignModal";
import FTSAuth from "./FTSAuth";
import FTSBtn from "../elements/FTSBtn";
import FTSCollapsibleElement from "../elements/FTSCollapsibleElement";
import FTSFormElement from "../elements/FTSFormElement";
import FTSSelect from "../elements/FTSSelect";
import FTSLabelGroup from "../elements/display/FTSLabelGroup";
import FTSFormDesigner from "../elements/FTSFormDesigner";
import FTSTransitionExpand from "../elements/FTSTransitionExpand";
import FTSTippy from "../elements/FTSTippy";
import FTSFormScript from "./FTSFormScript";
import FTSStateDesignInline from "./FTSStateDesignInline";
import FTSActivityDesignInline from "./FTSActivityDesignInline";

export default {
  components: {
    "fts-design-modal": FTSDesignModal,
    "fts-auth": FTSAuth,
    "fts-btn": FTSBtn,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-form-element": FTSFormElement,
    "fts-select": FTSSelect,
    "fts-label-group": FTSLabelGroup,
    "fts-form-designer": FTSFormDesigner,
    "fts-transition-expand": FTSTransitionExpand,
    "fts-tippy": FTSTippy,
    "fts-form-script": FTSFormScript,
    "fts-state-design-inline": FTSStateDesignInline,
    "fts-activity-design-inline": FTSActivityDesignInline,
  },
  data: () => ({
    formData: null,
    stateFormData: null,
    originalData: null,
    resolve: null,
    saveCallBack: null,
    navigateCallback: null,
    advanced: false,
    classificationFormData: {},
    formDesignPreview: false,
  }),
  validations() {
    let formData = {};
    formData = validator.standardDesign(); 
    return { formData };
  },
  methods: {
    async show(item, saveCallBack, navigateCallback) {
      this.saveCallBack = saveCallBack;
      this.navigateCallback = navigateCallback;
      this.formData = _.cloneDeep(item);
      /* form design */
      if (this.formData.design && this.formData.design.rows) {
        commons.expandWorkDesign(this.information, this.formData.design);
        if (!this.formData.design.payload) {
          this.$set(this.formData.design, "payload", {});
        }
      } else if (this.formData.design) {
        this.$delete(this.formData, "design");
      }
      /* state form data */
      if (this.formData.id === 'createEditView' || this.formData.id === 'create') {
        this.stateFormData = {};
        const state = this.states.find(x => x.initial);
        if (state && state.id) {
          this.$set(this.stateFormData, "id", state.id);
        }
      }

      return new Promise(resolve => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData)
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    },
                  },
                ],
              });             
            } else {
              this.reset();
            }
          },
        });

        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
        });

        if (
          (this.formData.id === 'createEditView' || this.formData.id === 'create') &&
          !Boolean(this.information && this.information.length)
        ) {
          this.createWorkDesign();
          this.$nextTick(() => {
            this.$refs.ftsFormDesigner.openInformationDesignModal();
          });
        }
      });
    },
    done(save) {
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        const formData = _.cloneDeep(this.formData);
        if (save) {
          this.saveCallBack && this.saveCallBack(formData);
          this.originalData = _.cloneDeep(this.formData);
        } else {
          this.resolve && this.resolve(formData);
          this.reset();
        }
      }
    },
    navigateContinue(type, item, params) {
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        const formData = _.cloneDeep(this.formData);
        const navigateCallback = Object.assign(this.navigateCallback);
        /* reset */
        this.reset();
        /* navigate */
        navigateCallback && navigateCallback(
          formData,
          type,
          item,
          params,
        );
      }
    },
    beforeClose() {
      this.$refs.ftsDesignModal.beforeClose();
    },
    remove() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: `Delete ${this.formData.name}?`,
        buttons: [
          { title: "Cancel" },
          {
            title: "Remove",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.resolve && this.resolve(true);
              this.reset();
            },
          },
        ],
      });  
    },
    reset() {
      this.$v.$reset();
      this.$refs.ftsDesignModal.close();
      Object.assign(this.$data, this.$options.data.apply(this));
      /* set route */
      if (this.$route.query.t || this.$route.query.tid) {
        let query = {...this.$route.query};
        delete query.t;
        delete query.tid;
        this.$router.push({ query: query })
      }
    },
    createWorkDesign() {
      this.$set(this.formData, "design", {
        rows: [{index: 0, items: []}],
        backendScript: "",
        payload: "{}",
      });
    },
    removeWorkDesign() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: "Remove form?",
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.$delete(this.formData, "design");
            },
          },
        ],
      });  
    },
    updateFormDesignMode() {
      this.$refs.ftsFormDesigner && this.$refs.ftsFormDesigner.previewDesign();
    },
    navigateStateDesign(id) {
      const result = this.states.find(x => x.id === id);
      if (result)
        this.navigateContinue(canvasDesign.state, result);
      else
        this.$toasted.error('Something went wrong. State not found.');
    },
    navigateNewActivityDesign(id) {
      const params = { fromStateId: id, disableContinue: true };
      this.navigateContinue(canvasDesign.activity, null, params);
    },
    openWorkflow() {
      this.$openStudioWorkflow();
    },
  },
  computed: {
    beta() {
      return this.$store.state.beta;
    },
    information() {
      return this.$store.getters.canvasInformation;
    },
    menus() {
      return this.$store.getters.canvasMenus.map(x => ({
        id: x.id,
        name: x.name,
        emoji: x.emoji,
      }));
    },
    states() {
      return this.$store.getters.canvasStates;
    },
    standardWithWorkDesign() {
      return this.formData && [
        "createEditView", "createUpdate", "create", "edit", "quickView", "view"
      ].indexOf(this.formData.id) > -1
    },
  },
}
</script>